import React, {useState, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import {DateRange} from 'react-date-range'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import {enUS} from 'date-fns/locale'

interface FilterComponentProps {
  startDate: moment.Moment
  endDate: moment.Moment
  viewType: 'weekly' | 'monthly'
  showDurationButton?: boolean
  onFilterChange: (viewType: 'weekly' | 'monthly', start: moment.Moment, end: moment.Moment) => void
}

const FilterComponent: React.FC<FilterComponentProps> = ({
  startDate,
  endDate,
  viewType,
  showDurationButton = true,
  onFilterChange,
}) => {
  const [view, setView] = useState<'weekly' | 'monthly'>(viewType)
  const [currentDate, setCurrentDate] = useState(moment(startDate))
  const [showCalendar, setShowCalendar] = useState(false)

  const formik = useFormik({
    initialValues: {
      viewType: view,
      startDate: moment(startDate).toISOString(),
      endDate: moment(endDate).toISOString(),
    },
    validationSchema: Yup.object({
      viewType: Yup.string().oneOf(['weekly', 'monthly']).required(),
      startDate: Yup.string().required(),
      endDate: Yup.string().required(),
    }),
    onSubmit: (values) => {
      // already handled manually
    },
  })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    formik.setFieldValue('viewType', viewType)
    formik.setFieldValue('startDate', moment(startDate).toISOString())
    formik.setFieldValue('endDate', moment(endDate).toISOString())
    setCurrentDate(moment(startDate))
    setView(viewType)
  }, [startDate, endDate, viewType])

  const handleViewChange = (type: 'weekly' | 'monthly') => {
    setView(type)
    formik.setFieldValue('viewType', type)

    const base = moment()
    const newStart =
      type === 'weekly' ? base.clone().startOf('week') : base.clone().startOf('month')
    const newEnd = type === 'weekly' ? base.clone().endOf('week') : base.clone().endOf('month')

    setCurrentDate(newStart)
    formik.setFieldValue('startDate', newStart.toISOString())
    formik.setFieldValue('endDate', newEnd.toISOString())
    onFilterChange(type, newStart, newEnd)
    formik.handleSubmit()
  }

  const goToPrevious = () => {
    const newDate =
      view === 'weekly'
        ? moment(currentDate).subtract(1, 'week')
        : moment(currentDate).subtract(1, 'month')
    updateDateRange(newDate)
  }

  const goToNext = () => {
    const newDate =
      view === 'weekly' ? moment(currentDate).add(1, 'week') : moment(currentDate).add(1, 'month')

    // Disable the next button if the new date is in the future (beyond today's date)
    if (newDate.isAfter(moment(), 'day')) {
      return // Do not proceed to update if the date is in the future
    }

    updateDateRange(newDate)
  }

  const updateDateRange = (baseDate: moment.Moment) => {
    // const newStart =
    //   view === 'weekly' ? baseDate.clone().startOf('week') : baseDate.clone().startOf('month')
    // const newEnd =
    //   view === 'weekly' ? baseDate.clone().endOf('week') : baseDate.clone().endOf('month')

    const newStart =
      view === 'weekly' ? baseDate.clone().startOf('week') : baseDate.clone().startOf('month')
    const newEnd =
      view === 'weekly' ? baseDate.clone().endOf('week') : baseDate.clone().endOf('month')

    setCurrentDate(newStart)
    formik.setFieldValue('startDate', newStart.toISOString())
    formik.setFieldValue('endDate', newEnd.toISOString())
    onFilterChange(view, newStart, newEnd)
    formik.handleSubmit()
  }

  const getDateLabel = () => {
    const start =
      view === 'weekly' ? moment(currentDate).startOf('week') : moment(currentDate).startOf('month')

    const end =
      view === 'weekly' ? moment(currentDate).endOf('week') : moment(currentDate).endOf('month')

    const today = moment()
    if (view === 'weekly') {
      if (start.isSame(today.startOf('week'), 'day')) return 'This week'
      if (start.isSame(today.clone().subtract(1, 'week').startOf('week'), 'day')) return 'Last week'
      return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`
    } else {
      return moment(currentDate).format('MMMM YYYY')
    }
  }

  const handleDateSelect = (ranges: any) => {
    const selectedStart = moment(ranges.selection.startDate)

    // const newStart =
    //   view === 'weekly'
    //     ? moment(selectedStart).startOf('week')
    //     : moment(selectedStart).startOf('month')

    // const newEnd =
    //   view === 'weekly' ? moment(selectedStart).endOf('week') : moment(selectedStart).endOf('month')

    const newStart =
      view === 'weekly'
        ? selectedStart.clone().startOf('week')
        : selectedStart.clone().startOf('month')
    const newEnd =
      view === 'weekly' ? selectedStart.clone().endOf('week') : selectedStart.clone().endOf('month')

    setCurrentDate(newStart)
    formik.setFieldValue('startDate', newStart.toISOString())
    formik.setFieldValue('endDate', newEnd.toISOString())
    onFilterChange(view, newStart, newEnd)
    setShowCalendar(false)
    formik.handleSubmit()
  }

  const selectionRange = {
    startDate:
      view === 'weekly'
        ? currentDate.clone().startOf('week').toDate()
        : currentDate.clone().startOf('month').toDate(),
    endDate:
      view === 'weekly'
        ? currentDate.clone().endOf('week').toDate()
        : currentDate.clone().endOf('month').toDate(),
    key: 'selection',
  }

  return (
    <div className='d-flex flex-col justify-content-between align-items-center text-white rounded p-3 position-relative'>
      {showDurationButton && (
        <div className='btn-group me-3 border rounded' role='group'>
          <button
            type='button'
            onClick={() => handleViewChange('weekly')}
            className={`btn btn-outline-light ${
              view === 'weekly' ? 'active bg-secondary text-white' : 'text-gray-600'
            }`}
          >
            Weekly
          </button>
          <button
            type='button'
            onClick={() => handleViewChange('monthly')}
            className={`btn btn-outline-light ${
              view === 'monthly' ? 'active bg-secondary text-white' : 'text-gray-600'
            }`}
          >
            Monthly
          </button>
        </div>
      )}

      <div className='d-flex align-items-center gap-2 border rounded p-2 position-relative ms-auto'>
        <button
          onClick={goToPrevious}
          className='btn btn-outline-light border text-gray-600 fw-bold'
        >
          {'<'}
        </button>

        <div
          className='d-flex align-items-center btn btn-outline-light border'
          onClick={() => setShowCalendar(!showCalendar)}
          style={{cursor: 'pointer'}}
        >
          <span role='img' aria-label='calendar' className='me-2 fw-bold'>
            📅
          </span>
          <span className='text-gray-600'>{getDateLabel()}</span>
        </div>

        <button onClick={goToNext} className='btn btn-outline-light border text-gray-600 fw-bold'>
          {'>'}
        </button>

        {showCalendar && (
          <div className='position-absolute top-100 end-0 mt-2 z-3 bg-white border rounded shadow'>
            <DateRange
              ranges={[selectionRange]}
              onChange={handleDateSelect}
              moveRangeOnFirstSelection={false}
              editableDateInputs={false}
              showMonthAndYearPickers={true}
              rangeColors={['#2563eb']}
              locale={enUS}
              maxDate={moment().toDate()} //to stop user from selecting next date than current date
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default FilterComponent
